/**
 * Support conversations. A ticket is the person's first message; everything
 * after it, from either side, is a support_ticket_messages row written only by
 * the support-message edge function. The person reads and answers under
 * Contact Support → My requests, where opening the list marks support's
 * messages read (mark_support_replies_seen, own rows only). Platform staff
 * answer from Admin Panel → Support.
 *
 * Email is a heads-up only, and for the person it is opt-in:
 * profiles.support_email_opt_in, default off, switched on and off by them.
 */
import { supabase } from '@/lib/safeSupabase';

export interface MySupportTicket {
  id: string;
  category: string;
  section: string | null;
  section_detail: string | null;
  subject: string;
  message: string;
  status: string;
  created_at: string;
  last_support_message_at: string | null;
  user_last_read_at: string | null;
}

export interface SupportMessage {
  id: string;
  ticket_id: string;
  author: 'user' | 'support';
  body: string;
  created_at: string;
}

export const MY_SUPPORT_TICKET_COLUMNS =
  'id, category, section, section_detail, subject, message, status, created_at, last_support_message_at, user_last_read_at';

/** Support wrote after the person last opened My requests. */
export function isUnreadReply(t: Pick<MySupportTicket, 'last_support_message_at' | 'user_last_read_at'>): boolean {
  if (!t.last_support_message_at) return false;
  if (!t.user_last_read_at) return true;
  return new Date(t.last_support_message_at).getTime() > new Date(t.user_last_read_at).getTime();
}

export function unreadReplies(tickets: MySupportTicket[] | null | undefined): MySupportTicket[] {
  return (tickets ?? []).filter(isUnreadReply);
}

export async function loadMySupportTickets(userId: string): Promise<MySupportTicket[]> {
  const { data, error } = await supabase
    .from('support_tickets' as never)
    .select(MY_SUPPORT_TICKET_COLUMNS)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(50);
  // Before migration 20260925000003 the new columns don't exist; show no
  // tickets rather than an error on every page.
  if (error) return [];
  return (data ?? []) as unknown as MySupportTicket[];
}

/** Every message on these tickets, oldest first. RLS limits it to the
 *  caller's own tickets, or all tickets for platform staff. */
export async function loadSupportMessages(ticketIds: string[]): Promise<SupportMessage[]> {
  if (ticketIds.length === 0) return [];
  const { data, error } = await supabase
    .from('support_ticket_messages' as never)
    .select('id, ticket_id, author, body, created_at')
    .in('ticket_id', ticketIds)
    .order('created_at', { ascending: true });
  if (error) return [];
  return (data ?? []) as unknown as SupportMessage[];
}

export async function markSupportRepliesSeen(): Promise<void> {
  await supabase.rpc('mark_support_replies_seen' as never);
}

/** Write in a support conversation. The server decides whether the caller
 *  writes as the ticket's author or as support; nothing here says which. */
export async function postSupportMessage(ticketId: string, body: string): Promise<{ saved: boolean; emailed: boolean }> {
  const { data, error } = await supabase.functions.invoke('support-message', {
    body: { ticket_id: ticketId, body },
  });
  if (error) {
    let message = error.message;
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === 'function') {
        const payload = await ctx.json();
        if (payload?.error) message = payload.error;
      }
    } catch { /* keep the generic message */ }
    throw new Error(message);
  }
  const out = (data ?? {}) as { saved?: boolean; emailed?: boolean; error?: string };
  if (out.error) throw new Error(out.error);
  return { saved: !!out.saved, emailed: !!out.emailed };
}

/** "Email me when support replies". Missing column (pre-migration) reads off. */
export async function loadSupportEmailOptIn(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('profiles')
    .select('support_email_opt_in' as never)
    .eq('id', userId)
    .maybeSingle();
  if (error || !data) return false;
  return (data as { support_email_opt_in?: boolean }).support_email_opt_in === true;
}

export async function setSupportEmailOptIn(userId: string, on: boolean): Promise<void> {
  const { data, error } = await supabase
    .from('profiles')
    .update({ support_email_opt_in: on } as never)
    .eq('id', userId)
    .select('id');
  if (error) throw error;
  // An RLS-refused UPDATE is zero rows, not an error. (Under View-as the
  // sandbox drops the write and data is null; nothing to report there.)
  if (Array.isArray(data) && data.length === 0) throw new Error('Could not save your email setting');
}

/** Admin Panel: the person wrote after support last did. */
export function awaitingSupport(t: { last_user_message_at?: string | null; last_support_message_at?: string | null }): boolean {
  if (!t.last_user_message_at) return false;
  if (!t.last_support_message_at) return true;
  return new Date(t.last_user_message_at).getTime() > new Date(t.last_support_message_at).getTime();
}
